import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, Loader2, Eye, Plus, X, Trash2, HandshakeIcon } from 'lucide-react';
import { numberToTextRo } from '@/utils/numberToTextRo';
import { CUILookupInput } from '@/components/CUILookupInput';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const statusLabels = { pending: 'In asteptare', in_processing: 'In procesare', completed: 'Finalizat', rejected: 'Respins' };
const CONTRACT_SERVICE_ID = 'srv_contract';

const ILFOV_LOCALITIES = [
  "1 Decembrie", "Afumati", "Balotesti", "Berceni", "Bragadiru",
  "Branesti", "Buftea", "Cernica", "Chiajna", "Chitila",
  "Ciolpani", "Ciorogirla", "Clinceni", "Copaceni", "Corbeanca",
  "Cornetu", "Darasti-Ilfov", "Dascalu", "Dobroesti", "Domnesti",
  "Dragomiresti-Vale", "Ganeasa", "Glina", "Gradistea", "Gruiu",
  "Jilava", "Magurele", "Moara Vlasiei", "Mogosoaia", "Nuci",
  "Otopeni", "Pantelimon", "Peris", "Petrachioaia", "Popesti-Leordeni",
  "Snagov", "Stefanestii de Jos", "Tunari", "Vidra", "Voluntari"
];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([{ vehicle_id: '', services: [] }]);
  const [deadline, setDeadline] = useState('');
  const [advancePaid, setAdvancePaid] = useState('');
  const [notes, setNotes] = useState('');

  // Contract data state
  const [contractData, setContractData] = useState({
    buyer_type: 'persoana_fizica',
    buyer_name: '',
    buyer_cnp_cui: '',
    buyer_id_document: '',
    buyer_address: '',
    localitate: '',
    pret_vanzare: '',
    pret_vanzare_moneda: 'EUR'
  });

  // Check if any vehicle has srv_contract selected
  const hasContractService = selectedVehicles.some(v => v.services.includes(CONTRACT_SERVICE_ID));

  const pretVanzareText = useMemo(() => {
    const num = parseFloat(contractData.pret_vanzare);
    if (!num || num <= 0) return '';
    return `${numberToTextRo(num)} ${contractData.pret_vanzare_moneda}`;
  }, [contractData.pret_vanzare, contractData.pret_vanzare_moneda]);

  useEffect(() => { fetchOrders(); }, [statusFilter]);

  useEffect(() => {
    if (dialogOpen) {
      fetchClients();
      fetchServices();
    }
  }, [dialogOpen]);

  useEffect(() => {
    if (selectedClientId) {
      fetchClientVehicles(selectedClientId);
    }
  }, [selectedClientId]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/orders', { 
        headers: { Authorization: 'Bearer ' + token }, 
        withCredentials: true, 
        params: statusFilter !== 'all' ? { status: statusFilter } : {} 
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea comenzilor');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/clients', {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchClientVehicles = async (clientId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(API + '/admin/vehicles', {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true,
        params: { client_id: clientId }
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(API + '/services');
      setServices(response.data.filter(s => s.is_active));
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const addVehicle = () => {
    setSelectedVehicles([...selectedVehicles, { vehicle_id: '', services: [] }]);
  };

  const removeVehicle = (index) => {
    const newVehicles = selectedVehicles.filter((_, i) => i !== index);
    setSelectedVehicles(newVehicles);
  };

  const updateVehicle = (index, field, value) => {
    const newVehicles = [...selectedVehicles];
    newVehicles[index][field] = value;
    setSelectedVehicles(newVehicles);
  };

  const toggleService = (vehicleIndex, serviceId) => {
    const newVehicles = [...selectedVehicles];
    const currentServices = newVehicles[vehicleIndex].services;
    if (currentServices.includes(serviceId)) {
      newVehicles[vehicleIndex].services = currentServices.filter(id => id !== serviceId);
    } else {
      newVehicles[vehicleIndex].services = [...currentServices, serviceId];
    }
    setSelectedVehicles(newVehicles);
  };

  const handleContractChange = (e) => {
    const { name, value } = e.target;
    setContractData(prev => ({ ...prev, [name]: value }));
  };

  const createOrder = async () => {
    if (!selectedClientId) {
      toast.error('Selectati un client');
      return;
    }
    
    const validVehicles = selectedVehicles.filter(v => v.vehicle_id && v.services.length > 0);
    if (validVehicles.length === 0) {
      toast.error('Adaugati cel putin un vehicul cu servicii');
      return;
    }

    if (hasContractService) {
      if (!contractData.buyer_name) {
        toast.error('Completati numele cumparatorului');
        return;
      }
      if (!contractData.buyer_cnp_cui) {
        toast.error('Completati CNP/CUI cumparator');
        return;
      }
      if (!contractData.localitate) {
        toast.error('Selectati localitatea');
        return;
      }
      if (!contractData.pret_vanzare || parseFloat(contractData.pret_vanzare) <= 0) {
        toast.error('Completati pretul de vanzare');
        return;
      }
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        client_id: selectedClientId,
        vehicles: validVehicles,
        deadline: deadline || null,
        advance_paid: parseFloat(advancePaid) || 0,
        notes: notes || ''
      };

      if (hasContractService) {
        payload.contract_data = {
          buyer_type: contractData.buyer_type,
          buyer_name: contractData.buyer_name,
          buyer_cnp_cui: contractData.buyer_cnp_cui,
          buyer_id_document: contractData.buyer_id_document || '',
          buyer_address: contractData.buyer_address || '',
          localitate: contractData.localitate,
          pret_vanzare: parseFloat(contractData.pret_vanzare),
          pret_vanzare_moneda: contractData.pret_vanzare_moneda,
          pret_vanzare_text: pretVanzareText
        };
      }

      await axios.post(API + '/admin/orders', payload, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      
      toast.success('Comanda creata cu succes');
      setDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea comenzii');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setSelectedClientId('');
    setSelectedVehicles([{ vehicle_id: '', services: [] }]);
    setDeadline('');
    setAdvancePaid('');
    setNotes('');
    setVehicles([]);
    setContractData({
      buyer_type: 'persoana_fizica',
      buyer_name: '',
      buyer_cnp_cui: '',
      buyer_id_document: '',
      buyer_address: '',
      localitate: '',
      pret_vanzare: '',
      pret_vanzare_moneda: 'EUR'
    });
  };

  const deleteOrder = async (orderId, orderNumber) => {
    if (!confirm(`Sigur doriti sa stergeti comanda "${orderNumber}"? Aceasta actiune va sterge si facturile asociate.`)) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(API + '/admin/orders/' + orderId, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      toast.success('Comanda stearsa');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la stergere');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Comenzi</h1>
          <p className="text-slate-500">Gestioneaza comenzile</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-order-btn">
              <Plus className="h-4 w-4 mr-2" />
              Adauga comanda
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adauga comanda noua</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Client Selection */}
              <div>
                <Label>Client *</Label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger data-testid="select-admin-client">
                    <SelectValue placeholder="Selecteaza client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name} - {client.cnp_cui}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicles and Services */}
              {selectedClientId && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Vehicule si servicii *</Label>
                    <Button size="sm" variant="outline" onClick={addVehicle}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adauga vehicul
                    </Button>
                  </div>
                  
                  {selectedVehicles.map((veh, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Vehicul {idx + 1}</Label>
                        {selectedVehicles.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeVehicle(idx)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      
                      <Select 
                        value={veh.vehicle_id} 
                        onValueChange={(v) => updateVehicle(idx, 'vehicle_id', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteaza vehicul" />
                        </SelectTrigger>
                        <SelectContent>
                          {vehicles.map(vehicle => (
                            <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                              {vehicle.brand} {vehicle.model} - {vehicle.vin}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {veh.vehicle_id && (
                        <div>
                          <Label className="text-sm mb-2 block">Servicii</Label>
                          <div className="grid grid-cols-2 gap-2">
                            {services.map(service => (
                              <label 
                                key={service.service_id} 
                                className="flex items-center gap-2 p-2 bg-white rounded border cursor-pointer hover:border-blue-500"
                              >
                                <input
                                  type="checkbox"
                                  checked={veh.services.includes(service.service_id)}
                                  onChange={() => toggleService(idx, service.service_id)}
                                  className="rounded"
                                />
                                <span className="text-sm flex-1">{service.name}</span>
                                <span className="text-sm font-medium text-blue-600">{service.price} EUR</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Contract Data Section - only shows when srv_contract selected */}
              {hasContractService && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4" data-testid="admin-contract-section">
                  <div className="flex items-center gap-2 mb-1">
                    <HandshakeIcon className="h-5 w-5 text-amber-600" />
                    <Label className="text-base font-semibold text-amber-800">Date contract vanzare-cumparare</Label>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Selecteaza cumparator din clienti existenti</Label>
                    <Select
                      value="__none__"
                      onValueChange={(clientId) => {
                        if (clientId === '__none__') return;
                        const c = clients.find(cl => cl.client_id === clientId);
                        if (c) {
                          setContractData(prev => ({
                            ...prev,
                            buyer_type: c.client_type || 'persoana_fizica',
                            buyer_name: c.name || '',
                            buyer_cnp_cui: c.cnp_cui || '',
                            buyer_id_document: c.id_document || '',
                            buyer_address: c.address || ''
                          }));
                        }
                      }}
                    >
                      <SelectTrigger data-testid="admin-select-buyer-from-clients">
                        <SelectValue placeholder="Selecteaza client existent..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Completare manuala --</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.client_id} value={c.client_id}>{c.name} - {c.cnp_cui}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm">Tip cumparator *</Label>
                    <Select
                      value={contractData.buyer_type}
                      onValueChange={(value) => setContractData(prev => ({ ...prev, buyer_type: value, buyer_name: '', buyer_cnp_cui: '', buyer_id_document: '', buyer_address: '' }))}
                    >
                      <SelectTrigger data-testid="admin-select-buyer-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="persoana_fizica">Persoana fizica</SelectItem>
                        <SelectItem value="persoana_juridica">Persoana juridica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">
                        {contractData.buyer_type === 'persoana_fizica' ? 'Nume complet cumparator' : 'Denumire firma cumparator'} *
                      </Label>
                      <Input
                        name="buyer_name"
                        value={contractData.buyer_name}
                        onChange={handleContractChange}
                        placeholder={contractData.buyer_type === 'persoana_fizica' ? 'ex: Maria Ionescu' : 'ex: SC Firma SRL'}
                        data-testid="admin-input-buyer-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">
                        {contractData.buyer_type === 'persoana_fizica' ? 'CNP cumparator' : 'CUI'} *
                      </Label>
                      {contractData.buyer_type === 'persoana_juridica' ? (
                        <CUILookupInput
                          value={contractData.buyer_cnp_cui}
                          onChange={(e) => setContractData(prev => ({ ...prev, buyer_cnp_cui: e.target.value }))}
                          onCompanyFound={(company) => setContractData(prev => ({
                            ...prev,
                            buyer_name: company.name || prev.buyer_name,
                            buyer_cnp_cui: company.cui || prev.buyer_cnp_cui,
                            buyer_address: company.address || prev.buyer_address,
                            buyer_id_document: company.registration_number || prev.buyer_id_document
                          }))}
                          data-testid="admin-input-buyer-cnp-cui"
                        />
                      ) : (
                        <Input
                          name="buyer_cnp_cui"
                          value={contractData.buyer_cnp_cui}
                          onChange={handleContractChange}
                          placeholder="2951234567890"
                          data-testid="admin-input-buyer-cnp-cui"
                        />
                      )}
                    </div>
                    <div>
                      <Label className="text-sm">
                        {contractData.buyer_type === 'persoana_fizica' ? 'Serie Numar CI Cumparator' : 'Nr. Reg. Comertului Cumparator'}
                      </Label>
                      <Input
                        name="buyer_id_document"
                        value={contractData.buyer_id_document}
                        onChange={handleContractChange}
                        placeholder={contractData.buyer_type === 'persoana_fizica' ? 'ex: RX 654321' : 'ex: J40/5678/2021'}
                        data-testid="admin-input-buyer-id-doc"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Adresa cumparator</Label>
                      <Input
                        name="buyer_address"
                        value={contractData.buyer_address}
                        onChange={handleContractChange}
                        placeholder="ex: Str. Exemplu nr. 10, Sector 1, Bucuresti"
                        data-testid="admin-input-buyer-address"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Localitate intocmire document *</Label>
                      <Select
                        value={ILFOV_LOCALITIES.includes(contractData.localitate) ? contractData.localitate : '__manual__'}
                        onValueChange={(value) => {
                          if (value !== '__manual__') setContractData(prev => ({ ...prev, localitate: value }));
                        }}
                      >
                        <SelectTrigger data-testid="admin-select-localitate">
                          <SelectValue placeholder="Selectati..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ILFOV_LOCALITIES.map((loc) => (
                            <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        name="localitate"
                        value={ILFOV_LOCALITIES.includes(contractData.localitate) ? '' : contractData.localitate}
                        onChange={(e) => setContractData(prev => ({ ...prev, localitate: e.target.value }))}
                        placeholder="Sau scrieti manual alta localitate..."
                        className="mt-1"
                        data-testid="admin-input-localitate-manual"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Pret vanzare *</Label>
                      <div className="flex gap-2">
                        <Input
                          name="pret_vanzare"
                          type="number"
                          min="0"
                          step="1"
                          value={contractData.pret_vanzare}
                          onChange={handleContractChange}
                          placeholder="ex: 5000"
                          className="flex-1"
                          data-testid="admin-input-pret-vanzare"
                        />
                        <Select
                          value={contractData.pret_vanzare_moneda}
                          onValueChange={(value) => setContractData(prev => ({ ...prev, pret_vanzare_moneda: value }))}
                        >
                          <SelectTrigger className="w-24" data-testid="admin-select-moneda">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="RON">RON</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Pret vanzare (text)</Label>
                      <div
                        className="h-10 flex items-center px-3 bg-amber-100 border border-amber-300 rounded-md text-amber-900 font-medium text-sm"
                        data-testid="admin-pret-vanzare-text"
                      >
                        {pretVanzareText || 'Completati pretul...'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Termen limita</Label>
                  <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
                </div>
                <div>
                  <Label>Avans platit (EUR)</Label>
                  <Input 
                    type="number" 
                    placeholder="0" 
                    value={advancePaid} 
                    onChange={(e) => setAdvancePaid(e.target.value)} 
                  />
                </div>
              </div>

              <div>
                <Label>Notite</Label>
                <Textarea 
                  placeholder="Adauga observatii..." 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Anuleaza
                </Button>
                <Button 
                  onClick={createOrder} 
                  disabled={creating}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="admin-create-order-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Se creeaza...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Creeaza comanda
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-[200px]"><SelectValue placeholder="Toate statusurile" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Toate</SelectItem>
          <SelectItem value="pending">In asteptare</SelectItem>
          <SelectItem value="in_processing">In procesare</SelectItem>
          <SelectItem value="completed">Finalizat</SelectItem>
        </SelectContent>
      </Select>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : orders.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nicio comanda</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.order_id} className="bg-white hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold">{order.order_number}</p>
                        <Badge variant="secondary">{statusLabels[order.status] || order.status}</Badge>
                        {order.contract_data && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">Contract</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{order.client?.name || 'N/A'}</p>
                      <p className="text-sm text-slate-400">
                        {order.vehicles?.length || 0} vehicul(e) - {new Date(order.created_at).toLocaleDateString('ro-RO')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-bold">{order.total_price} EUR</p>
                    <Link to={`/admin/orders/${order.order_id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-2" />
                        Detalii
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => deleteOrder(order.order_id, order.order_number)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
